/**
 * Client-side RBAC utilities
 * Role hierarchy and permission checking
 */

export type DialgiaRole = 'dialgia_admin' | 'dialgia_pm' | 'dialgia_dev';
export type ClientRole = 'client' | 'client_admin' | 'client_user';
export type SystemRole = 'super_admin' | 'admin' | 'manager' | 'user' | 'candidate';
export type AppRole = DialgiaRole | ClientRole | SystemRole;

export const APP_ROLES: readonly AppRole[] = [
  'user',
  'client',
  'admin',
  'manager',
  'super_admin',
  'candidate',
  'dialgia_admin',
  'dialgia_pm',
  'dialgia_dev',
  'client_admin',
  'client_user',
];

export const isAppRole = (role: string): role is AppRole => (
  (APP_ROLES as readonly string[]).includes(role)
);

// Role hierarchy - higher index = more permissions
const ROLE_HIERARCHY: Record<AppRole, number> = {
  'user': 0,
  'candidate': 0,
  'client': 1,
  'client_user': 1,
  'client_admin': 2,
  'dialgia_dev': 3,
  'manager': 4,
  'dialgia_pm': 5,
  'dialgia_admin': 6,
  'admin': 7,
  'super_admin': 8,
};

// Role groups for easy checking
export const DIALGIA_STAFF_ROLES: AppRole[] = [
  'super_admin', 'admin', 'dialgia_admin', 'dialgia_pm', 'dialgia_dev', 'manager'
];

export const CLIENT_ROLES: AppRole[] = ['user', 'client', 'client_admin', 'client_user'];

export const ADMIN_ROLES: AppRole[] = ['super_admin', 'admin', 'dialgia_admin'];

/**
 * Check if user has a specific role
 */
export function hasRole(userRoles: AppRole[], requiredRole: AppRole): boolean {
  return userRoles.includes(requiredRole);
}

/**
 * Check if user has any of the required roles
 */
export function hasAnyRole(userRoles: AppRole[], requiredRoles: AppRole[]): boolean {
  return requiredRoles.some(role => userRoles.includes(role));
}

/**
 * Check if user has all of the required roles
 */
export function hasAllRoles(userRoles: AppRole[], requiredRoles: AppRole[]): boolean {
  return requiredRoles.every(role => userRoles.includes(role));
}

/**
 * Check if user is Dialgia staff
 */
export function isDialgiaStaff(userRoles: AppRole[]): boolean {
  return hasAnyRole(userRoles, DIALGIA_STAFF_ROLES);
}

/**
 * Check if user is a client (not Dialgia staff)
 */
export function isClientUser(userRoles: AppRole[]): boolean {
  return hasAnyRole(userRoles, CLIENT_ROLES) && !isDialgiaStaff(userRoles);
}

/**
 * Check if user has admin-level access
 */
export function isAdmin(userRoles: AppRole[]): boolean {
  return hasAnyRole(userRoles, ADMIN_ROLES);
}

/**
 * Get the highest role from a list of roles
 */
export function getHighestRole(userRoles: AppRole[]): AppRole {
  if (userRoles.length === 0) return 'user';
  
  return userRoles.reduce((highest, current) => {
    return (ROLE_HIERARCHY[current] || 0) > (ROLE_HIERARCHY[highest] || 0) ? current : highest;
  }, userRoles[0]);
}

/**
 * Check if a role meets minimum level
 */
export function meetsRoleLevel(userRoles: AppRole[], minimumRole: AppRole): boolean {
  const highestRole = getHighestRole(userRoles);
  return (ROLE_HIERARCHY[highestRole] || 0) >= (ROLE_HIERARCHY[minimumRole] || 0);
}

/**
 * Permission definitions for features
 */
export interface FeaturePermissions {
  canViewTickets: boolean;
  canCreateTickets: boolean;
  canManageTickets: boolean;
  canViewProjects: boolean;
  canManageProjects: boolean;
  canViewWorkspace: boolean;
  canManageWorkspace: boolean;
  canViewAdmin: boolean;
  canManageUsers: boolean;
  canManageRoles: boolean;
  canViewAuditLogs: boolean;
  canManageSettings: boolean;
  canImpersonate: boolean;
}

/**
 * Get permissions for a set of roles
 */
export function getPermissions(userRoles: AppRole[]): FeaturePermissions {
  const isStaff = isDialgiaStaff(userRoles);
  const isAdminUser = isAdmin(userRoles);
  const isClientAdminUser = hasRole(userRoles, 'client_admin');
  const isPM = hasAnyRole(userRoles, ['dialgia_pm', 'dialgia_admin', 'admin', 'super_admin']);
  
  return {
    // Ticket permissions
    canViewTickets: true, // Everyone can view their own tickets
    canCreateTickets: true, // Everyone can create tickets
    canManageTickets: isStaff, // Only staff can manage all tickets
    
    // Project permissions
    canViewProjects: true, // Everyone can view their projects
    canManageProjects: isPM, // Only PMs+ can manage projects
    
    // Workspace permissions
    canViewWorkspace: true, // Everyone can view their workspace
    canManageWorkspace: isClientAdminUser || isStaff,
    
    // Admin permissions
    canViewAdmin: isStaff,
    canManageUsers: isAdminUser,
    canManageRoles: hasAnyRole(userRoles, ['super_admin', 'admin']),
    canViewAuditLogs: isAdminUser,
    canManageSettings: isAdminUser,
    
    // Special permissions
    canImpersonate: hasRole(userRoles, 'super_admin'),
  };
}

/**
 * Route guard - returns redirect path if access denied
 */
export function checkRouteAccess(
  userRoles: AppRole[],
  route: string
): { allowed: boolean; redirectTo?: string } {
  const permissions = getPermissions(userRoles);
  
  // Admin routes
  if (route.startsWith('/admin')) {
    if (!permissions.canViewAdmin) {
      return { allowed: false, redirectTo: '/dashboard' };
    }
  }
  
  // User management
  if (route.includes('/users') || route.includes('/roles')) {
    if (!permissions.canManageUsers) {
      return { allowed: false, redirectTo: '/admin' };
    }
  }
  
  // Audit logs
  if (route.includes('/audit')) {
    if (!permissions.canViewAuditLogs) {
      return { allowed: false, redirectTo: '/admin' };
    }
  }
  
  // Impersonation
  if (route.includes('/impersonation')) {
    if (!permissions.canImpersonate) {
      return { allowed: false, redirectTo: '/admin' };
    }
  }
  
  return { allowed: true };
}
